import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, Clock, Mail, Phone } from "lucide-react";
import PageHead from "@/components/PageHead";

function fmt(iso) {
  return new Date(iso).toLocaleString();
}

export default function VerifyRecipient() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState("choose"); // choose | code
  const [channel, setChannel] = useState(null);
  const [sentHint, setSentHint] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    publicApi.meta(token).then(setMeta).catch(() => setMeta({ status: "not_found" }));
  }, [token]);

  const requestCode = async (ch) => {
    setBusy(true);
    try {
      const res = await publicApi.requestCode(token, ch);
      setChannel(ch);
      setSentHint(res.hint);
      setStep("code");
      toast.success(`Code sent to ${res.hint}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) toast.error("Too many requests. Please wait a bit.");
      else toast.error(typeof detail === "string" ? detail : "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await publicApi.verifyCode(token, code);
      sessionStorage.setItem(`cv_view_${token}`, res.view_token);
      sessionStorage.setItem(
        `cv_meta_${token}`,
        JSON.stringify({
          title: res.title,
          message: res.message,
          recipient_name: res.recipient_name,
          sender_display: res.sender_display,
        })
      );
      toast.success("Verified");
      navigate(`/watch/${token}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 410) {
        toast.error("Code expired — request a new one");
        setStep("choose");
        setCode("");
      } else if (status === 429) {
        toast.error("Too many attempts — request a new code");
        setStep("choose");
        setCode("");
      } else {
        toast.error(typeof detail === "string" ? detail : "Incorrect code");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!meta) return <div className="p-12 text-white/40"><PageHead title="Verify | ChronoVaults" noindex />Loading...</div>;
  if (meta.status === "not_found") {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <PageHead title="Capsule not found | ChronoVaults" noindex />
        <Lock className="mx-auto w-8 h-8 text-white/40 mb-6" strokeWidth={1.2} />
        <h1 className="font-serif text-4xl text-bone">Capsule not found</h1>
        <p className="text-white/50 mt-3 text-sm">This link is invalid or has been revoked.</p>
      </div>
    );
  }

  if (meta.sealed) {
    return (
      <div className="min-h-[calc(100vh-88px)] grid lg:grid-cols-2">
        <PageHead title="Capsule sealed | ChronoVaults" noindex />
        <div className="relative hidden lg:block border-r border-white/5">
          <img
            src="https://images.unsplash.com/photo-1453828423292-392a660a502f?crop=entropy&cs=srgb&fm=jpg&h=650&w=940"
            alt="Wooden chest"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-vault-950 via-vault-950/60 to-transparent" />
        </div>
        <div className="p-12 flex items-center">
          <div className="max-w-md">
            <p className="label-mono text-terracotta">Capsule sealed</p>
            <h1 className="font-serif text-5xl text-bone mt-4 leading-tight">
              A memory is waiting for you, {meta.recipient_name}.
            </h1>
            <div className="mt-10 p-6 border border-white/10 bg-vault-800">
              <p className="label-mono flex items-center gap-2"><Clock className="w-3 h-3" /> Opens on</p>
              <p className="font-serif text-3xl text-terracotta mt-3">{fmt(meta.release_at)}</p>
            </div>
            <p className="text-sm text-white/50 mt-6">
              Bookmark this page and return after the release time to unseal the video.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Released — OTP flow
  return (
    <div className="min-h-[calc(100vh-88px)] grid lg:grid-cols-2">
      <PageHead title="Verify your identity | ChronoVaults" noindex />
      <div className="relative hidden lg:block border-r border-white/5">
        <img
          src="https://images.unsplash.com/photo-1453828423292-392a660a502f?crop=entropy&cs=srgb&fm=jpg&h=650&w=940"
          alt="Chest of memories"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-vault-950 via-vault-950/70 to-transparent" />
        <div className="relative z-10 h-full flex items-end p-14">
          <div>
            <p className="label-mono text-terracotta">Now Unlockable</p>
            <p className="font-serif text-5xl text-bone mt-4 leading-tight">
              A message from<br />
              <span className="text-terracotta italic font-light">{meta.sender_display}</span>.
            </p>
          </div>
        </div>
      </div>

      <div className="p-8 flex items-center">
        {step === "choose" ? (
          <div className="w-full max-w-md" data-testid="verify-channel-picker">
            <p className="label-mono text-terracotta">Identity check</p>
            <h1 className="font-serif text-4xl text-bone mt-3 leading-tight">
              Hello, {meta.recipient_name}.
            </h1>
            <p className="text-sm text-white/60 mt-4 mb-8 leading-relaxed">
              To unseal your message, we'll send a one-time 6-digit code to the address the sender registered for you.
              Choose where to receive it.
            </p>

            <div className="space-y-3">
              {meta.channels?.email?.available && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => requestCode("email")}
                  data-testid="verify-channel-email"
                  className="w-full text-left px-5 py-4 border border-white/10 hover:border-terracotta/50 bg-vault-800 transition-colors flex items-center gap-4 disabled:opacity-50"
                >
                  <Mail className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm text-bone">Send code by email</p>
                    <p className="text-xs text-white/50 font-mono">{meta.channels.email.hint}</p>
                  </div>
                </button>
              )}
              {meta.channels?.sms?.available && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => requestCode("sms")}
                  data-testid="verify-channel-sms"
                  className="w-full text-left px-5 py-4 border border-white/10 hover:border-terracotta/50 bg-vault-800 transition-colors flex items-center gap-4 disabled:opacity-50"
                >
                  <Phone className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm text-bone">Send code by text message</p>
                    <p className="text-xs text-white/50 font-mono">{meta.channels.sms.hint}</p>
                  </div>
                </button>
              )}
            </div>

            <p className="mt-6 text-xs text-white/40">
              Codes expire in 10 minutes. You get 3 attempts per code. Sender contact details are never asked.
            </p>
          </div>
        ) : (
          <form onSubmit={submitCode} className="w-full max-w-md" data-testid="verify-code-form">
            <p className="label-mono text-terracotta">Enter code</p>
            <h1 className="font-serif text-4xl text-bone mt-3 leading-tight">Check your {channel === "email" ? "inbox" : "phone"}.</h1>
            <p className="text-sm text-white/60 mt-4 mb-8">
              We sent a 6-digit code to <span className="font-mono text-bone">{sentHint}</span>. Enter it below.
            </p>

            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              data-testid="verify-code-input"
              className="h-14 text-center font-mono text-2xl tracking-[0.5em]"
            />

            <Button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="verify-code-submit"
              className="mt-6 w-full h-12 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm tracking-widest text-xs uppercase disabled:bg-vault-700 disabled:text-white/40"
            >
              {busy ? "Verifying..." : "Unseal capsule"}
            </Button>

            <div className="mt-6 flex justify-between text-xs">
              <button
                type="button"
                onClick={() => { setStep("choose"); setCode(""); }}
                data-testid="verify-back"
                className="text-white/50 hover:text-terracotta"
              >
                ← Choose a different channel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => requestCode(channel)}
                data-testid="verify-resend"
                className="text-white/50 hover:text-terracotta"
              >
                Resend code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
